import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Card } from "primereact/card";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import CustomeButton from "~/components/Button";
import {
  useGetTenantsId,
  usePatchTenantsId,
  type GetTenantsId200Data,
} from "~/api/generated/api";
import { useQueryClient } from "@tanstack/react-query";
import { AccountManagerSection } from "../onboarding/components/Step2CustomerAdmin/AccountManagerSection";
import { CustomerAdminForm } from "../onboarding/components/Step2CustomerAdmin/CustomerAdminForm";
import {
  tenantOnboardingActions,
  tenantOnboardingStore,
} from "../onboarding/store/tenantOnboardingStore";
import { useStoreSelector } from "~/lib/store/hooks";
import {
  validateAccountManager,
  validateStep2,
} from "../onboarding/validation/step2Validation";
import ToastMessage from "~/components/ToastMessage";
import apiService from "~/services/service";
import API_ROUTES from "~/api-routes-v2";
import { AddAccountManagerModal } from "~/Pages/Tenants/modals/AddAccountManagerModal";
import { triggerGlobalToast } from "~/utils/globalToast";
import type {
  AccountManagerOption,
  NewAccountManagerData,
} from "~/Pages/Tenants/types/tenants";
import type { CustomerAdminFormData } from "../onboarding/utils/formatters";
import { getCountryISO2From3CharCode } from "~/utils/countries";
import AuditSidebar from "~/components/audit-sidebar/AuditSidebar";
import { UserRole } from "~/types/roles";

const buildCustomerAdmin = (
  tenantData?: GetTenantsId200Data | null,
): CustomerAdminFormData => {
  const admin = tenantData?.customer_admin;
  const mappedCountry =
    admin?.country && admin.country.length === 3
      ? getCountryISO2From3CharCode(admin.country) || admin.country
      : admin?.country;

  return {
    given_name: admin?.given_name || "",
    surname: admin?.surname || "",
    other_name: admin?.other_name || "",
    preferred_name: admin?.preferred_name || "",
    contact_email: admin?.contact_email || "",
    country: mappedCountry || "",
    phone_code: admin?.phone_code || "",
    phone_number: admin?.phone_number || "",
  };
};

const buildAccountManager = (
  tenantData?: GetTenantsId200Data | null,
): AccountManagerOption | null => {
  const manager = tenantData?.account_manager;
  if (!manager || !manager.id) return null;

  const name =
    manager.preferred_name ||
    [manager.given_name, manager.surname].filter(Boolean).join(" ");

  return {
    id: manager.id,
    name: name || manager.email || "",
    email: manager.email || "",
  };
};

const mapAccountManagerFormData = (
  option: AccountManagerOption | null,
): {
  account_manager_id: string;
  account_manager_email: string;
  is_new_account_manager: boolean;
} => ({
  account_manager_id: option?.id || "",
  account_manager_email: option?.email || "",
  is_new_account_manager: false,
});

const combineErrors = (
  adminErrors: Record<string, string[]>,
  managerErrors: Record<string, string[]>,
) => ({
  ...adminErrors,
  ...managerErrors,
});

const isEqual = (a: any, b: any) =>
  JSON.stringify(a, (key, value) =>
    value instanceof Date ? value.toISOString() : value,
  ) ===
  JSON.stringify(b, (key, value) =>
    value instanceof Date ? value.toISOString() : value,
  );

const TenantAdminInformation: React.FC = () => {
  const { id: tenantId } = useParams();
  const toastRef = useRef<any>(null);
  const { role } = useSelector((state: any) => state.login);
  const queryClient = useQueryClient();

  const { data: tenantResponse, isLoading } = useGetTenantsId(tenantId!);
  const tenantData = tenantResponse?.data?.data;

  // Check if user is GXM Admin or Super Admin
  const isGXMAdmin =
    role === UserRole.GXM_ADMIN || role === UserRole.SUPER_ADMIN;

  const step2Data = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.step2Data,
  );
  const accountManagerData = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.accountManagerData,
  );
  const adminInformation = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.adminInformation,
  );
  const accountManagerInformation = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.accountManagerInformation,
  );
  const validationErrors = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.validationErrors,
  );
  const touchedFields = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.touchedFields,
  );
  const isEditing = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.isEditing,
  );

  const [accountManagers, setAccountManagers] = useState<
    AccountManagerOption[]
  >([]);
  const [isLoadingAccountManagers, setIsLoadingAccountManagers] =
    useState(false);
  const [hasFetchedAccountManagers, setHasFetchedAccountManagers] =
    useState(false);
  const [showAddAccountManagerModal, setShowAddAccountManagerModal] =
    useState(false);
  const [isAddingAccountManager, setIsAddingAccountManager] = useState(false);
  const [isAuditSidebarOpen, setIsAuditSidebarOpen] = useState(false);

  const initialAccountManagerOption = useMemo(
    () => buildAccountManager(tenantData),
    [tenantData],
  );

  useEffect(() => {
    if (tenantData) {
      const adminData = buildCustomerAdmin(tenantData);
      const managerData = mapAccountManagerFormData(
        initialAccountManagerOption,
      );

      tenantOnboardingActions.setStep2Data(adminData);
      tenantOnboardingActions.setAccountManagerData(managerData);
      tenantOnboardingActions.setAdminInformation(adminData);
      tenantOnboardingActions.setAccountManagerInformation(managerData);
      tenantOnboardingActions.setIsEditing(false);
      tenantOnboardingActions.clearTouchedFields();
      tenantOnboardingActions.setValidationErrors({});

      if (initialAccountManagerOption) {
        setAccountManagers((prev) => {
          const exists = prev.some(
            (m) => m.id === initialAccountManagerOption.id,
          );
          return exists ? prev : [...prev, initialAccountManagerOption];
        });
      }
    }
  }, [tenantData, initialAccountManagerOption]);

  const fetchAccountManagers = useCallback(async () => {
    if (hasFetchedAccountManagers || isLoadingAccountManagers) return;

    setIsLoadingAccountManagers(true);
    try {
      const response: any = await apiService(
        API_ROUTES.FETCH_ACCOUNT_MANAGERS,
        "GET",
      );
      if (response?.data) {
        const managers = response.data.map((manager: any) => ({
          id: manager.id,
          name:
            manager.preferred_name ||
            [manager.given_name, manager.surname].filter(Boolean).join(" "),
          email: manager.email,
        }));
        setAccountManagers((prev) => {
          const merged = [...prev];
          managers.forEach((m: AccountManagerOption) => {
            if (!merged.find((existing) => existing.id === m.id)) {
              merged.push(m);
            }
          });
          return merged;
        });
      }
      setHasFetchedAccountManagers(true);
    } catch (error) {
      console.error("Failed to fetch account managers:", error);
    } finally {
      setIsLoadingAccountManagers(false);
    }
  }, [hasFetchedAccountManagers, isLoadingAccountManagers]);

  const handleEnableEdit = () => {
    tenantOnboardingActions.setIsEditing(true);
  };

  const handleDiscardEdit = () => {
    if (adminInformation) {
      tenantOnboardingActions.updateStep2Data(adminInformation);
    }
    if (accountManagerInformation) {
      tenantOnboardingActions.updateAccountManagerData(
        accountManagerInformation,
      );
    }
    tenantOnboardingActions.setIsDirty(false);
    tenantOnboardingActions.setIsEditing(false);
    tenantOnboardingActions.clearTouchedFields();
    tenantOnboardingActions.setValidationErrors({});
  };

  const handleCustomerAdminChange = (
    updates: Partial<CustomerAdminFormData>,
  ) => {
    const fieldName = Object.keys(updates)[0];
    const newState = { ...step2Data, ...updates };
    tenantOnboardingActions.updateStep2Data(updates);

    if (fieldName) {
      tenantOnboardingActions.markFieldTouched(fieldName);
    }

    const adminErrors = validateStep2(newState);
    const managerErrors = validateAccountManager(accountManagerData);

    const filteredErrors: Record<string, string[]> = {};
    Object.keys(adminErrors).forEach((key) => {
      if (touchedFields.has(key) || key === fieldName) {
        filteredErrors[key] = adminErrors[key];
      }
    });
    Object.keys(managerErrors).forEach((key) => {
      if (touchedFields.has(key) || key === fieldName) {
        filteredErrors[key] = managerErrors[key];
      }
    });

    tenantOnboardingActions.setValidationErrors(filteredErrors);
  };

  const handleAccountManagerChange = (
    updates: Partial<typeof accountManagerData>,
  ) => {
    const fieldName = Object.keys(updates)[0];
    const newState = { ...accountManagerData, ...updates };
    tenantOnboardingActions.updateAccountManagerData(updates);

    if (fieldName) {
      tenantOnboardingActions.markFieldTouched(fieldName);
    }

    const adminErrors = validateStep2(step2Data);
    const managerErrors = validateAccountManager(newState);

    const filteredErrors: Record<string, string[]> = {};
    Object.keys(adminErrors).forEach((key) => {
      if (touchedFields.has(key) || key === fieldName) {
        filteredErrors[key] = adminErrors[key];
      }
    });
    Object.keys(managerErrors).forEach((key) => {
      if (touchedFields.has(key) || key === fieldName) {
        filteredErrors[key] = managerErrors[key];
      }
    });

    tenantOnboardingActions.setValidationErrors(filteredErrors);
  };

  const handleBlur = (fieldName: string) => {
    tenantOnboardingActions.markFieldTouched(fieldName);

    const adminErrors = validateStep2(step2Data);
    const managerErrors = validateAccountManager(accountManagerData);
    const filteredErrors: Record<string, string[]> = {};

    Object.keys(adminErrors).forEach((key) => {
      if (touchedFields.has(key) || fieldName === key) {
        filteredErrors[key] = adminErrors[key];
      }
    });

    Object.keys(managerErrors).forEach((key) => {
      if (touchedFields.has(key) || fieldName === key) {
        filteredErrors[key] = managerErrors[key];
      }
    });

    tenantOnboardingActions.setValidationErrors(filteredErrors);
  };

  const handleAccountManagerSelect = (manager: AccountManagerOption) => {
    tenantOnboardingActions.updateAccountManagerData({
      account_manager_id: manager.id,
      account_manager_email: manager.email,
      is_new_account_manager: false,
    });
    toastRef.current?.show({
      severity: "success",
      summary: `"${manager.name}"`,
      detail: (
        <span style={{ color: "var(--message-success-color, #1F2937)" }}>
          has been registered as Account Manager
        </span>
      ),
      life: 5000,
      icon: "pi pi-check",
    });
  };

  const handleAddAccountManagerSubmit = async (data: NewAccountManagerData) => {
    setIsAddingAccountManager(true);
    try {
      const response: any = await apiService(
        API_ROUTES.CREATE_ACCOUNT_MANAGER,
        "POST",
        {
          body: {
            given_name: data.given_name,
            surname: data.surname || undefined,
            other_name: data.other_name || undefined,
            preferred_name: data.preferred_name || undefined,
            country: data.country,
            email: data.email,
            phone_code: data.phone_code || undefined,
            phone_number: data.phone_number || undefined,
          },
        },
      );

      if (response?.data) {
        const newManager: AccountManagerOption = {
          id: response.data.id,
          name: [response.data.given_name, response.data.surname]
            .filter(Boolean)
            .join(" "),
          email: response.data.email,
        };

        setAccountManagers((prev) => [...prev, newManager]);

        tenantOnboardingActions.updateAccountManagerData({
          account_manager_id: newManager.id,
          account_manager_email: newManager.email,
          is_new_account_manager: true,
        });

        setShowAddAccountManagerModal(false);

        toastRef.current?.show({
          severity: "success",
          summary: `"${newManager.name}"`,
          detail: (
            <span style={{ color: "var(--message-success-color, #1F2937)" }}>
              has been registered as Account Manager registered.
            </span>
          ),
          life: 5000,
          icon: "pi pi-check",
        });
      }
    } catch (error) {
      console.error("Failed to add account manager:", error);
      triggerGlobalToast({
        severity: "error",
        summary: "Error",
        detail: "We couldn't add the Account Manager. Please try again.",
        life: 5000,
      });
    } finally {
      setIsAddingAccountManager(false);
    }
  };

  const fieldErrors = useMemo(() => {
    const adminErrors = validateStep2(step2Data);
    const managerErrors = validateAccountManager(accountManagerData);
    return combineErrors(adminErrors, managerErrors);
  }, [step2Data, accountManagerData]);

  const hasChanges =
    !isEqual(step2Data, adminInformation) ||
    !isEqual(accountManagerData, accountManagerInformation);
  const hasValidationErrors = Object.keys(fieldErrors).length > 0;
  const canSave = hasChanges && !hasValidationErrors;

  const editTenant = usePatchTenantsId({
    mutation: {
      onSuccess: (response) => {
        // Update query cache directly with PATCH response data (full tenant object)
        queryClient.setQueryData(["getTenantsId", tenantId], response);
        tenantOnboardingActions.setAdminInformation(step2Data);
        tenantOnboardingActions.setAccountManagerInformation(
          accountManagerData,
        );
        tenantOnboardingActions.setIsEditing(false);
        toastRef?.current?.show({
          severity: "success",
          summary: "Admin Information",
          detail: "has been updated!",
          icon: "pi pi-check",
        });
      },
    },
  });

  const handleSave = () => {
    editTenant.mutate({
      id: tenantId!,
      data: {
        customer_admin: step2Data as any,
        account_manager_id: accountManagerData.account_manager_id || undefined,
      },
    });
  };

  const handleSaveChanges = () => {
    tenantOnboardingActions.setAdminInformation(step2Data);
    tenantOnboardingActions.setAccountManagerInformation(accountManagerData);
    tenantOnboardingActions.setIsDirty(false);
    handleSave();
  };

  return (
    <Card className="gen-card core-tenant-info__main-content">
      <div className="flex align-items-center justify-content-between">
        <p className="heading">Admin Information</p>
        <div className="flex gap-1">
          {isEditing ? (
            <>
              <CustomeButton
                label="Discard"
                variant="outlined"
                className="mr-2"
                onClick={handleDiscardEdit}
              />

              <CustomeButton
                label="Save Changes"
                variant="contained"
                onClick={handleSaveChanges}
                loading={editTenant.isPending}
                disabled={!canSave || editTenant.isPending}
                className={!canSave ? "disabled-btn" : ""}
                iconPos={"right"}
              />
            </>
          ) : (
            <>
              {isGXMAdmin && (
                <CustomeButton
                  label="View History"
                  variant="outlined"
                  className="mr-2"
                  icon="pi pi-history"
                  iconPos="right"
                  onClick={() => setIsAuditSidebarOpen(true)}
                  disabled={isLoading}
                />
              )}
              <CustomeButton
                label="Edit"
                variant="contained"
                className="mr-2 access-btn"
                onClick={handleEnableEdit}
                icon="pi pi-pencil"
                iconPos="right"
                disabled={isLoading}
              />
            </>
          )}
        </div>
      </div>

      <div className="admin-information-form">
        <AccountManagerSection
          data={accountManagerData}
          onChange={handleAccountManagerChange}
          onBlur={handleBlur}
          errors={validationErrors}
          touchedFields={touchedFields}
          accountManagers={accountManagers}
          onAddNewAccountManager={() => setShowAddAccountManagerModal(true)}
          onDropdownShow={fetchAccountManagers}
          onAccountManagerSelect={handleAccountManagerSelect}
          isLoading={isLoadingAccountManagers}
          disabled={!isEditing || isLoading}
        />

        <CustomerAdminForm
          data={step2Data}
          onChange={handleCustomerAdminChange}
          onBlur={handleBlur}
          errors={validationErrors}
          touchedFields={touchedFields}
          disabled={!isEditing || isLoading}
        />
      </div>

      <AddAccountManagerModal
        visible={showAddAccountManagerModal}
        onHide={() => setShowAddAccountManagerModal(false)}
        onSubmit={handleAddAccountManagerSubmit}
        isLoading={isAddingAccountManager}
        existingEmails={accountManagers.map((m) => m.email.toLowerCase())}
      />

      <ToastMessage ref={toastRef} />

      {isGXMAdmin && isAuditSidebarOpen && (
        <AuditSidebar
          heading="Admin Information"
          endpoint={API_ROUTES.FETCH_TENANT_ADMIN_INFO_AUDIT}
          id={tenantId}
          objectType="ADMIN_INFORMATION"
          labels={() => ({
            create: "Admin Information Created",
            update: "Information Updated",
            delete: "Admin Information Deleted",
          })}
          nameLink={null}
          setSidebarToggle={setIsAuditSidebarOpen}
          sidebarToggle={isAuditSidebarOpen}
          overrideName={(tenantData as any)?.tenant_name || ""}
        />
      )}
    </Card>
  );
};

export default TenantAdminInformation;
